import { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createEmptyClientDocument } from '../../api/user';
import { PublicRoutes } from '../../routes/navigation';
import { loadUserThunk } from '../../redux/user/thunk';
import { parseAttributionParams } from '../../utils/parseAttributionParams';
import { isOnFunnelStartRoute } from '../utils/isOnFunnelStartRoute';
import { useAuthFlow } from './useAuthFlow';
import { updateUserMetadataAnalyticsParams } from 'src/api/user-metadata';
import useToast from 'src/context/ToastContext/ToastContext';

export const useFunnelUserIdentity = (
  isFlowInited: boolean,
  flowDefined: boolean,
  isDefaultFlow: boolean,
  flowId?: string
) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { triggerSignInAnonymouslyFlow, authUser } = useAuthFlow();
  const doNothingCount = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInit, setIsInit] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    initLogic();
  }, [flowId, authUser, isLoading, isFlowInited, isDefaultFlow]);

  const initLogic = useCallback(async () => {
    setIsInit(false);
    try {
      if (!flowId) {
        return;
      }
      if (!isFlowInited) {
        return;
      }
      if (!flowDefined) {
        return;
      }
      if (isLoading) {
        return;
      }

      if (authUser?.uid) {
        await dispatch(loadUserThunk(authUser.uid));
      }

      if (isDefaultFlow) {
        return;
      }

      if (!authUser) {
        if (isOnFunnelStartRoute()) {
          signInAnonymous();
        } else {
          const searchParams = new URLSearchParams(window.location.search);
          history.replace(PublicRoutes.ON_BOARDING_FLOW.replace(':flowId', flowId) + `?${searchParams.toString()}`);
          window?.location?.reload();
        }
      } else {
        if (doNothingCount.current === 0) {
          await updateUserMetadataAnalyticsParams();
        }
        if (!authUser?.email) {
          doNothingCount.current++;
        }
      }
    } catch (error) {
    } finally {
      setIsInit(true);
    }
  }, [authUser, flowId, isLoading, isFlowInited, flowDefined, isDefaultFlow]);

  const signInAnonymous = () => {
    setIsLoading(true);
    triggerSignInAnonymouslyFlow()
      .then((userCredential) => {
        const anonymousUserData = userCredential?.user;
        if (!anonymousUserData?.uid) {
          throw new Error('Anonymous user uid is not defined');
        }
        createClientDocument(anonymousUserData);
      })
      .catch((error) => {
        showToast({ error });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const createClientDocument = async (anonymousUserData: any) => {
    if (!anonymousUserData?.uid) {
      return;
    }
    const attributionParams = parseAttributionParams();
    await createEmptyClientDocument(anonymousUserData.uid, {
      app_lang: i18n.language,
      ...attributionParams,
    });
  };

  return { isInit };
};
